/**
 * Browser-only helpers for the installable app (service worker + install prompt).
 * Call them from effects or event handlers only.
 */

interface InstallPromptEvent extends Event {
    prompt: () => Promise<void>
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

let deferredPrompt: InstallPromptEvent | null = null
const INSTALL_EVENT = 'germa:install-available'

/** Registers the service worker on the published site; removes it in the preview so edits always show. */
export const setupServiceWorker = (): void => {
    if (!('serviceWorker' in navigator)) return

    if (process.env.NODE_ENV !== 'production') {
        void navigator.serviceWorker.getRegistrations().then((registrations) => {
            registrations.forEach((registration) => void registration.unregister())
        })
        return
    }

    const register = () => {
        void navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => {
            // Without a service worker the site still works, only not offline.
        })
    }
    if (document.readyState === 'complete') register()
    else window.addEventListener('load', register, { once: true })
}

/** Keeps the browser's "install app" offer so a button can show it later. */
export const listenForInstallPrompt = (): (() => void) => {
    const onPrompt = (event: Event) => {
        event.preventDefault()
        deferredPrompt = event as InstallPromptEvent
        window.dispatchEvent(new Event(INSTALL_EVENT))
    }
    const onInstalled = () => {
        deferredPrompt = null
        window.dispatchEvent(new Event(INSTALL_EVENT))
    }
    window.addEventListener('beforeinstallprompt', onPrompt)
    window.addEventListener('appinstalled', onInstalled)
    return () => {
        window.removeEventListener('beforeinstallprompt', onPrompt)
        window.removeEventListener('appinstalled', onInstalled)
    }
}

export const canInstall = (): boolean => deferredPrompt !== null

export const onInstallAvailabilityChange = (callback: () => void): (() => void) => {
    window.addEventListener(INSTALL_EVENT, callback)
    return () => window.removeEventListener(INSTALL_EVENT, callback)
}

export const promptInstall = async (): Promise<boolean> => {
    const prompt = deferredPrompt
    if (!prompt) return false
    await prompt.prompt()
    const { outcome } = await prompt.userChoice
    deferredPrompt = null
    window.dispatchEvent(new Event(INSTALL_EVENT))
    return outcome === 'accepted'
}

/** Asks the service worker to keep this book's PDF for reading without internet. */
export const saveBookOffline = (slug: string, title: string): void => {
    if (!('serviceWorker' in navigator)) return
    void navigator.serviceWorker.ready
        .then((registration) => {
            registration.active?.postMessage({
                type: 'cache-book',
                url: `/api/libra/${encodeURIComponent(slug)}/pdf`,
                slug,
                title
            })
        })
        .catch(() => {})
}

export interface OfflineBook {
    slug: string
    title: string
    url: string
    savedAt: number
}

/** The books saved for offline reading (most recent first). */
export const getOfflineBooks = async (): Promise<OfflineBook[]> => {
    if (!('caches' in window)) return []
    try {
        const response = await caches.match('/__germa-offline-books.json')
        if (!response) return []
        const list = (await response.json()) as OfflineBook[]
        return Array.isArray(list) ? list : []
    } catch {
        return []
    }
}
